import React, { useEffect, useRef } from 'react';
import { Configure, Index, useHits, useInstantSearch } from 'react-instantsearch';
import { SearchProductCardWrapper } from './SearchProductCardWrapper';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { toCloudRetailProductDetail } from '@jbhi-fi/utils/src/ga4Tracking/functions/toCloudRetailProductDetail';

import { baseConfig } from '../../config/algolia';
import { postAlgoliaProductClick } from '../../utils/algolia/search-insights';
import { AlgoliaProductHit } from '../../utils/types';

const { product_families_index } = baseConfig;

const PRODUCTS_LIMIT = 4;

export const RecentlyViewedResults = ({ storedProducts }: { storedProducts: Array<number> }) => {
  const { status } = useInstantSearch();
  const { hits, results: searchResults } = useHits<AlgoliaProductHit>();
  const searching = status === 'loading';
  const isSearchStalled = status === 'stalled';
  const hasResults = searchResults && searchResults.nbHits !== 0;
  const noResults = searchResults?.nbHits === 0 && !searching && !isSearchStalled;
  const isDesktop = useBreakpoint('md', 'up');
  const sortedHits = hits
    .sort((a, b) => {
      const skuA = a.sku || a.skus?.[0];
      const skuB = b.sku || b.skus?.[0];
      return storedProducts.indexOf(Number(skuA)) - storedProducts.indexOf(Number(skuB));
    })
    .slice(0, PRODUCTS_LIMIT);

  const hitsKey = sortedHits.map(h => h.objectID).join(',');
  const prevHitsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hasResults) return;
    if (hitsKey === prevHitsKeyRef.current) return;
    prevHitsKeyRef.current = hitsKey;

    trackViewItemList({
      currency: getStoreCurrency(),
      listName: 'search - recently viewed',
      searchType: 'suggested search',
      viewedItems: transformAlgoliaHitForTracking(sortedHits),
      cloudRetail: {
        productDetails: sortedHits.map(toCloudRetailProductDetail),
        filter: null,
      },
    });
  }, [hasResults, hitsKey]);

  const productCards = sortedHits.map((hit, index) => (
    <SearchProductCardWrapper
      type="modal"
      hit={hit}
      key={hit.variant_id}
      position={index + 1}
      onClick={() => postAlgoliaProductClick(hit.sku, index + 1)}
      variant="small"
    />
  ));

  if (noResults) {
    return <div key="3">No results</div>;
  } else if (hasResults) {
    return (
      <>
        {isDesktop ? (
          productCards
        ) : (
          <Carousel showPagination={false} slidesPerView="auto" fullBleed>
            {productCards}
          </Carousel>
        )}
      </>
    );
  } else {
    return (
      <>
        {[...Array(PRODUCTS_LIMIT)].map((e, i) => (
          <LoadingSkeleton.ProductCardSkeleton shouldShimmer variant="small" key={i} />
        ))}
        ;
      </>
    );
  }
};

export const RecentlyViewedResultsWrapper = ({ storedProducts }: { storedProducts: Array<number> }) => {
  if (storedProducts.length <= 0) {
    return null;
  }

  const filters = `${storedProducts
    .map(sku => `skus:${sku} OR sku:${sku}`)
    .join(' OR ')} AND (price > 0 AND product_published = 1 AND availability.displayProduct = 1)`;

  return (
    <Index indexName={product_families_index ?? ''} indexId="recentlyViewed">
      <Configure clickAnalytics hitsPerPage={12} filters={filters} distinct />
      <RecentlyViewedResults storedProducts={storedProducts} />
    </Index>
  );
};
